import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  MenuItem,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import ProjectCard from '../components/ProjectCard';
import ProjectForm from '../components/ProjectForm';
import GeneratePlanDialog from '../components/GeneratePlanDialog';
import DesktopImportExport from '../components/DesktopImportExport';
import { projectService, taskService, templateService } from '../services/api';
import { useTranslation } from 'react-i18next';

const isElectron = typeof window !== 'undefined' && window.electronAPI?.isElectron === true;

function ProjectList() {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Formulaire projet
  const [formOpen, setFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showArchived, setShowArchived] = useState(false);

  // Dialogue de confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteAction, setDeleteAction] = useState('archive'); // 'archive' or 'delete'

  // Générateur de planning IA
  const [generatePlanOpen, setGeneratePlanOpen] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    filterProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, searchQuery, filterPriority, filterStatus, showArchived]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectService.getAll();
      setProjects(response.data.projects);
      setError('');
    } catch (err) {
      setError(t('projects.errorLoading'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = [...projects];

    // Filtre par archive/actif
    if (showArchived) {
      filtered = filtered.filter(p => p.status === 'archived');
    } else {
      filtered = filtered.filter(p => p.status !== 'archived');
    }

    // Recherche par titre ou description
    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filtre par priorité
    if (filterPriority !== 'all') {
      filtered = filtered.filter(p => p.priority === filterPriority);
    }

    // Filtre par statut
    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => p.status === filterStatus);
    }

    setFilteredProjects(filtered);
  };

  const handleCreateProject = () => {
    setIsEdit(false);
    setEditingProject(null);
    setFormOpen(true);
  };

  const handleEditProject = (project) => {
    setIsEdit(true);
    setEditingProject(project);
    setFormOpen(true);
  };

  const handleDeleteProject = (project) => {
    setProjectToDelete(project);
    setDeleteAction('archive');
    setDeleteDialogOpen(true);
  };

  const handleDeletePermanently = (project) => {
    setProjectToDelete(project);
    setDeleteAction('delete');
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteAction === 'delete') {
        await projectService.deletePermanently(projectToDelete.id);
        setSuccess('Projet supprimé définitivement');
      } else {
        await projectService.delete(projectToDelete.id);
        setSuccess('Projet archivé avec succès');
      }
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
      fetchProjects();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 
        (deleteAction === 'delete' ? 'Erreur lors de la suppression' : 'Erreur lors de l\'archivage');
      setError(errorMsg);
      setDeleteDialogOpen(false);
    }
  };

  const handlePinProject = async (project) => {
    try {
      await projectService.update(project.id, { is_pinned: !project.is_pinned });
      // Mettre à jour la liste locale pour un feedback immédiat
      setProjects(projects.map(p => 
        p.id === project.id ? { ...p, is_pinned: !p.is_pinned } : p
      ));
    } catch (err) {
      console.error('Erreur lors de l\'épinglage du projet:', err);
      setError(t('projects.errorPin'));
    }
  };

  const handleSubmitProject = async (formData) => {
    try {
      if (isEdit) {
        await projectService.update(editingProject.id, formData);
        setSuccess(t('projects.successUpdated'));
      } else {
        if (formData.isTemplate) {
          await templateService.createProject(formData.templateId, {
            title: formData.title,
            start_date: formData.start_date,
            priority: formData.priority
          });
          setSuccess(t('projects.successCreated'));
        } else {
          await projectService.create(formData);
          setSuccess(t('projects.successCreated'));
        }
      }
      setFormOpen(false);
      setEditingProject(null);
      fetchProjects();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || t('projects.errorSave'));
    }
  };

  const handlePlanGenerated = async (plan) => {
    try {
      setLoading(true);

      const projectData = {
        title: plan.project.title,
        description: plan.project.description,
        priority: plan.project.priority || 'medium',
        status: 'active',
        start_date: new Date().toISOString().split('T')[0]
      };

      const projectResponse = await projectService.create(projectData);
      const projectId = projectResponse.data.project.id;

      if (plan.tasks && plan.tasks.length > 0) {
        const startBase = new Date();
        for (let i = 0; i < plan.tasks.length; i++) {
          const task = plan.tasks[i];
          const startDate = new Date(startBase);
          startDate.setDate(startDate.getDate() + (task.start_offset_days || 0));
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + (task.duration_days || 1));

          await taskService.create({
            project_id: projectId,
            title: task.title || 'Tâche sans titre',
            description: task.description || '',
            priority: task.priority || 'medium',
            status: 'not_started',
            phase: task.phase || null,
            estimated_hours: task.estimated_hours || 8,
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0],
            order_index: i
          });
        }
      }

      setSuccess(`Projet "${plan.project.title}" créé avec succès ! ${plan.tasks?.length || 0} tâches générées.`);
      fetchProjects();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error creating project from plan:', err);
      setError(err.response?.data?.error || 'Erreur lors de la création du projet depuis le planning IA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: 'transparent' }}>
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 2, md: 4 } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                {showArchived ? t('projects.titleArchived') : t('projects.titleMy')}
              </Typography>
              {showArchived && (
                <Chip
                  icon={<ArchiveIcon />}
                  label={t('projects.archivesChip')}
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
            {!showArchived && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {isElectron ? (
                  <DesktopImportExport
                    projects={projects}
                    onImported={() => { fetchProjects(); setSuccess('Projet importé avec succès'); }}
                  />
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => setGeneratePlanOpen(true)}
                    sx={{
                      borderColor: '#667eea',
                      color: '#667eea',
                      '&:hover': {
                        borderColor: '#764ba2',
                        backgroundColor: 'rgba(102, 126, 234, 0.04)'
                      }
                    }}
                  >
                    {t('projects.generateWithEra')}
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateProject}
                >
                  {t('projects.createProjectButton')}
                </Button>
              </Box>
            )}
          </Box>

          {/* Alertes */}
          {error && (
            <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Filtres */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder={t('projects.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('projects.filterPriorityLabel')}</InputLabel>
                <Select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  label={t('projects.filterPriorityLabel')}
                >
                  <MenuItem value="all">{t('projects.priorityAll')}</MenuItem>
                  <MenuItem value="low">{t('projects.priorityLow')}</MenuItem>
                  <MenuItem value="medium">{t('projects.priorityMedium')}</MenuItem>
                  <MenuItem value="high">{t('projects.priorityHigh')}</MenuItem>
                  <MenuItem value="urgent">{t('projects.priorityUrgent')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('projects.filterStatusLabel')}</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label={t('projects.filterStatusLabel')}
                >
                  <MenuItem value="all">{t('projects.statusAll')}</MenuItem>
                  <MenuItem value="active">{t('projects.statusActive')}</MenuItem>
                  <MenuItem value="on_hold">{t('projects.statusOnHold')}</MenuItem>
                  <MenuItem value="completed">{t('projects.statusCompleted')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Statistiques rapides */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {showArchived ? (
              <Chip
                label={t('projects.statsArchived', { count: projects.filter(p => p.status === 'archived').length })}
                color="warning"
              />
            ) : (
              <>
                <Chip label={t('projects.statsTotal', { count: projects.filter(p => p.status !== 'archived').length })} />
                <Chip
                  label={t('projects.statsActive', { count: projects.filter(p => p.status === 'active').length })}
                  color="success"
                />
                <Chip
                  label={t('projects.statsCompleted', { count: projects.filter(p => p.status === 'completed').length })}
                  color="info"
                />
                <Chip
                  label={t('projects.statsArchived', { count: projects.filter(p => p.status === 'archived').length })}
                  color="warning"
                  icon={<ArchiveIcon />}
                  onClick={() => setShowArchived(true)}
                  sx={{ cursor: 'pointer' }}
                />
              </>
            )}
          </Box>
        </Box>

        {/* Liste des projets */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredProjects.length > 0 ? (
          <Grid container spacing={3}>
            {filteredProjects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <ProjectCard
                  project={project}
                  onEdit={handleEditProject}
                  onDelete={handleDeleteProject}
                  onDeletePermanently={handleDeletePermanently}
                  onPin={handlePinProject}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchQuery || filterPriority !== 'all' || filterStatus !== 'all'
                ? t('projects.emptyFiltered')
                : t('projects.emptyNoProject')}
            </Typography>
            {!searchQuery && filterPriority === 'all' && filterStatus === 'all' && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleCreateProject}
                sx={{ mt: 2 }}
              >
                {t('projects.emptyCreateFirst')}
              </Button>
            )}
          </Box>
        )}
      </Container>

      {/* Formulaire de projet */}
      <ProjectForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingProject(null);
        }}
        onSubmit={handleSubmitProject}
        project={editingProject}
        isEdit={isEdit}
      />

      {/* Dialogue de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {deleteAction === 'delete' ? t('projects.confirmDeleteTitle') : t('projects.confirmArchiveTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteAction === 'delete' 
              ? t('projects.confirmDeleteText', { title: projectToDelete?.title || '' })
              : t('projects.confirmArchiveText', { title: projectToDelete?.title || '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('projects.cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {deleteAction === 'delete' ? t('projects.delete') : t('projects.archive')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Générateur de planning IA */}
      <GeneratePlanDialog
        open={generatePlanOpen}
        onClose={() => setGeneratePlanOpen(false)}
        onPlanGenerated={handlePlanGenerated}
      />
    </Box>
  );
}

export default ProjectList;
